import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchVideoDebugImageBlobUrl } from '../api';
import type { SampledFrame } from '../types';
import { positiveModulo, sampledFrameDebugPath } from '../utils/videoSampling';

interface FramesViewerProps {
  jobId: string;
  frames: SampledFrame[];
}

interface LoadedFrame {
  src: string;
  timestamp: number;
}

export function FramesViewer({ jobId, frames }: FramesViewerProps) {
  const [index, setIndex] = useState(0);
  const [loaded, setLoaded] = useState<Array<LoadedFrame | null>>([]);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{ startX: number; startIndex: number; pixelsPerFrame: number } | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;
    const created: string[] = [];
    setLoaded(Array.from({ length: frames.length }, () => null));
    setIndex(0);

    void Promise.all(
      frames.map(async (frame, i) => {
        if (!frame.Path) return;
        try {
          const src = await fetchVideoDebugImageBlobUrl(jobId, sampledFrameDebugPath(frame.Path));
          created.push(src);
          if (cancelled) return;
          setLoaded((prev) => {
            const next = prev.length === frames.length ? [...prev] : Array(frames.length).fill(null);
            next[i] = { src, timestamp: frame.Timestamp };
            return next;
          });
        } catch {
          // Leave the slot empty; the caption still names the missing still.
        }
      }),
    );

    return () => {
      cancelled = true;
      created.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [frames, jobId]);

  const count = frames.length;
  const current = loaded[index];

  const beginDrag = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      const width = e.currentTarget.clientWidth || 1;
      dragRef.current = {
        startX: e.clientX,
        startIndex: index,
        pixelsPerFrame: Math.max(8, width / Math.max(count, 1)),
      };
      setIsDragging(true);
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Pointer already lost — drag still tracks via move/up.
      }
    },
    [count, index],
  );

  const updateDrag = useCallback(
    (clientX: number) => {
      const drag = dragRef.current;
      if (!drag || count < 1) return;
      const delta = Math.round((clientX - drag.startX) / drag.pixelsPerFrame);
      setIndex(positiveModulo(drag.startIndex + delta, count));
    },
    [count],
  );

  const endDrag = useCallback(() => {
    dragRef.current = null;
    setIsDragging(false);
  }, []);

  return (
    <div className="bg-black">
      <div
        className={`relative select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        onPointerDown={beginDrag}
        onPointerMove={(e) => {
          if (dragRef.current) updateDrag(e.clientX);
        }}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {current ? (
          <img
            src={current.src}
            alt={`Sampled frame ${index + 1} of ${count}`}
            draggable={false}
            className="w-full max-h-[75vh] mx-auto block pointer-events-none"
          />
        ) : (
          <div className="min-h-[40vh] flex items-center justify-center text-white text-sm">
            Loading frames…
          </div>
        )}
      </div>
      <div className="px-4 py-3 flex items-center justify-between gap-3 text-white/80 text-xs bg-black/60">
        <div className="font-mono whitespace-nowrap">
          {count
            ? `frame ${index + 1}/${count} · ${(frames[index]?.Timestamp ?? 0).toFixed(2)}s`
            : '—'}
        </div>
        <div className="opacity-70 whitespace-nowrap">drag through extracted frames</div>
      </div>
    </div>
  );
}
